const RED_GRADIENT = {
  type: "linear",
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: "#E73F3F" },
    { offset: 1, color: "#FFA4A4" },
  ],
};

const BLUE_GRADIENT = {
  type: "linear",
  x: 0,
  y: 0,
  x2: 0,
  y2: 1,
  colorStops: [
    { offset: 0, color: "#1A4C9D" },
    { offset: 1, color: "#70A5FB" },
  ],
};

/** Rounded top corners for upward bars */
const BAR_BORDER_RADIUS = [4, 4, 0, 0];

export function normalizeProjectionValueSuffix(suffix) {
  if (suffix === undefined || suffix === null) {
    return "";
  }
  return String(suffix).trim();
}

export function parseProjectionNumericValue(value) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const normalized = String(value).replace(/,/g, "").trim();
  if (normalized === "" || normalized === "-") {
    return null;
  }
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

export function formatProjectionValue(val, currencySymbol = "£", suffix = "") {
  const num = parseProjectionNumericValue(val);
  if (num === null) {
    return "";
  }

  const normalizedSuffix = normalizeProjectionValueSuffix(suffix).toUpperCase();
  const symbol = currencySymbol || "£";
  const negative = num < 0 ? "-" : "";
  const abs = Math.abs(num);

  if (normalizedSuffix === "M") {
    return `${negative}${symbol}${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (normalizedSuffix === "K") {
    return `${negative}${symbol}${(abs / 1_000).toFixed(1)}K`;
  }

  if (abs >= 1_000_000) {
    return `${negative}${symbol}${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  }
  if (abs >= 10_000) {
    return `${negative}${symbol}${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return `${negative}${symbol}${abs.toLocaleString("en-US")}`;
}

function getRawValue(dataItem) {
  if (dataItem && typeof dataItem === "object" && dataItem.rawValue !== undefined) {
    return dataItem.rawValue;
  }
  return dataItem;
}

export function buildProjectionBarChartOption({
  title,
  subTitle,
  categories,
  values,
  currencySymbol = "£",
  valueSuffix = "",
}) {
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeValues = Array.isArray(values) ? values : [];
  const normalizedSuffix = normalizeProjectionValueSuffix(valueSuffix);

  const barData = safeValues.map((val, idx) => {
    const numeric = parseProjectionNumericValue(val) ?? 0;
    const rawValue = numeric;
    return {
      value: Math.abs(numeric),
      rawValue: numeric,
      itemStyle: {
        borderRadius: BAR_BORDER_RADIUS,
        color: idx === 0 ? RED_GRADIENT : BLUE_GRADIENT,
      },
    };
  });

  const titleBlock = title
    ? {
        text: title,
        subtext: subTitle || "",
        left: "0",
        textStyle: { color: "#FFFFFF", fontWeight: 700, fontSize: 16 },
        subtextStyle: { color: "rgba(255,255,255,0.7)", fontSize: 12 },
      }
    : undefined;

  return {
    backgroundColor: "transparent",
    title: titleBlock,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "rgba(16, 24, 40, 0.9)",
      borderColor: "rgba(255,255,255,0.08)",
      textStyle: { color: "#E7F0FF" },
      formatter: (params) => {
        if (!Array.isArray(params) || !params.length) return "";
        const p = params[0];
        const raw = getRawValue(p.data);
        return `${p.axisValue}<br/>${formatProjectionValue(
          raw,
          currencySymbol,
          normalizedSuffix
        )}`;
      },
    },
    grid: {
      left: 32,
      right: 32,
      top: title ? 56 : 40,
      bottom: 36,
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: safeCategories,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: "#FFFFFF",
          fontSize: 14,
          fontWeight: 500,
          margin: 12,
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        min: 0,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        type: "bar",
        barCategoryGap: "20px",
        data: barData,
        label: {
          show: true,
          position: "top",
          distance: 10,
          formatter: (params) =>
            formatProjectionValue(
              getRawValue(params.data),
              currencySymbol,
              normalizedSuffix
            ),
          color: "#FFFFFF",
          fontSize: 16,
          fontWeight: 700,
        },
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.3)" },
        },
      },
    ],
  };
}
